import { createContext, use, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { useProfile, type Profile } from './Providers/ProfileProvider';
import { track } from '@/shared/utils/track';

const ProtectedProfileContext = createContext<Profile | null>(null);

// The non-null profile for everything under ProtectedRoute — the subtree
// renders only when a profile exists, so pages consume this instead of
// useProfile().profile and never write logged-out branches.
export function useProtectedProfile(): Profile {
  const profile = use(ProtectedProfileContext);
  if (!profile) {
    throw new Error('useProtectedProfile must be used under ProtectedRoute');
  }
  return profile;
}

export default function ProtectedRoute() {
  const { profile, isAuthReady } = useProfile();
  const location = useLocation();
  const navigate = useNavigate();

  // One navigation per bounce, from the effect: the router's <Navigate>
  // re-navigates on every re-render while the redirect is still pending.
  const bounced = isAuthReady && !profile;
  useEffect(() => {
    if (bounced) {
      track('app_bounce_to_landing', { path: location.pathname });
      // Keep the query string — deep links may carry UTMs / invite codes.
      void navigate({ to: '/', search: location.search, replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per bounce, not per path change mid-redirect
  }, [bounced]);

  if (!isAuthReady || !profile) return null;
  return (
    <ProtectedProfileContext value={profile}>
      <Outlet />
    </ProtectedProfileContext>
  );
}
